import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useCertificationTypes } from '@/hooks/useCertificationTypes';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface MultiCertificationSelectorProps {
  selectedCertificationIds: string[];
  onChange: (certIds: string[]) => void;
}

export function MultiCertificationSelector({
  selectedCertificationIds,
  onChange,
}: MultiCertificationSelectorProps) {
  const { types, isLoading } = useCertificationTypes();

  const toggleCertification = (certId: string) => {
    if (selectedCertificationIds.includes(certId)) {
      onChange(selectedCertificationIds.filter(id => id !== certId));
    } else {
      onChange([...selectedCertificationIds, certId]);
    }
  };

  const removeCertification = (certId: string) => {
    onChange(selectedCertificationIds.filter(id => id !== certId));
  };

  const selectedCerts = types.filter(t => selectedCertificationIds.includes(t.id));

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">Loading certifications...</div>;
  }

  return (
    <div className="space-y-2">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            className="w-full justify-between"
          >
            {selectedCertificationIds.length === 0
              ? "Select certifications..."
              : `${selectedCertificationIds.length} selected`}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0" align="start">
          <Command>
            <CommandInput placeholder="Search certifications..." />
            <CommandEmpty>No certification found.</CommandEmpty>
            <CommandGroup className="max-h-64 overflow-auto">
              {types.map((cert) => (
                <CommandItem
                  key={cert.id}
                  value={cert.name}
                  onSelect={() => toggleCertification(cert.id)}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      selectedCertificationIds.includes(cert.id)
                        ? "opacity-100"
                        : "opacity-0"
                    )}
                  />
                  {cert.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>

      {selectedCerts.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedCerts.map((cert) => (
            <Badge
              key={cert.id}
              variant="secondary"
              className="gap-1"
            >
              {cert.name}
              <button
                type="button"
                onClick={() => removeCertification(cert.id)}
                className="ml-1 rounded-full hover:bg-muted"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
